import { cn } from '@client/utils/styling/cn';
import type { CSSProperties } from 'react';

type SpinnerSize = 'small' | 'medium' | 'large';

export interface SpinnerProps {
    /**
     * 16, 24 or 32px. Leave unset when the spinner is an Ant Design `Spin` indicator: `Spin` sizes
     * its indicator at `1em` from the `dotSize*` theme tokens, which carry the same three sizes.
     */
    size?: SpinnerSize;
    /** `accent` for page and section activity; `muted` beside inline status text. */
    tone?: 'accent' | 'muted';
    /** Announced to screen readers. Pass `null` when visible text beside the spinner already says it. */
    label?: string | null;
    /**
     * Set when the spinner is an Ant Design `Spin` indicator: `Spin` already wraps it in a polite live
     * region, so the spinner stays a plain label. Rendered on its own, a labelled spinner is its own
     * `role="status"` live region.
     */
    indicator?: boolean;
    className?: string;
    style?: CSSProperties;
}

// Stroke widths in viewBox units, so the ring reads as 2, 2.5 and 3px at 16, 24 and 32px.
const STROKE_WIDTH = { small: 3, medium: 2.5, large: 2.25 } as const satisfies Record<SpinnerSize, number>;
const SIZE_CLASS = { small: 'size-4', medium: 'size-6', large: 'size-8' } as const satisfies Record<
    SpinnerSize,
    string
>;

/**
 * The app's loading spinner: a short arc turning on a faint track of the same colour. The colour is
 * `currentColor`, so the accent tone follows a white-labelled page's `--color-accent-500`.
 *
 * Usually rendered through `Loading`; the `UIProvider` also supplies it as the default Ant Design
 * `Spin` indicator. Both pass `indicator`, so the announcement comes from `Spin`'s live region.
 */
export function Spinner({
    size,
    tone = 'accent',
    label = 'Loading',
    indicator = false,
    className,
    style,
}: SpinnerProps) {
    const strokeWidth = STROKE_WIDTH[size ?? 'medium'];
    const isStatus = !indicator && Boolean(label);

    return (
        <span
            className={cn(
                'relative inline-block shrink-0',
                size ? SIZE_CLASS[size] : 'size-[1em]',
                tone === 'accent' ? 'text-accent-500' : 'text-muted-foreground',
                className,
            )}
            style={style}
            role={isStatus ? 'status' : undefined}
        >
            <svg
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
                className="block size-full animate-spin [animation-duration:0.8s] motion-reduce:[animation-duration:2s]"
            >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity={0.18} strokeWidth={strokeWidth} />
                <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    pathLength={100}
                    strokeDasharray="28 100"
                    transform="rotate(-90 12 12)"
                />
            </svg>
            {label && <span className="sr-only">{label}</span>}
        </span>
    );
}

export type { SpinnerSize };
